import {MediaMatcher} from '@angular/cdk/layout';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit, signal,
  ViewChild
} from '@angular/core';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  first,
  map,
  observeOn,
  shareReplay,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';
import {
  AffiliateAdRequest,
  AffiliateExclusiveOption,
  AffiliateRelationshipMerchant,
  DownloadRequestBody,
  DownloadRequestContent,
  DownloadRequestContentColumn,
  EntityGroupName,
  MerchantAd,
  MerchantAdRequest,
  TagExclusiveOption
} from '../../core/dtos/api';
import {DashboardApiService} from '../../core/services/api/dashboard-api.service';
import {asyncScheduler, BehaviorSubject, combineLatest, Observable, of, Subject} from 'rxjs';
import {UntypedFormBuilder, UntypedFormControl} from '@angular/forms';
import {MerchantApiService} from '../../core/services/api/merchant-api.service';
import {AdManagementService} from './ad-management.service';
import {MasterToggleState, FilterChip, FilterMap, MerchantCommon} from './ad-management';
import {MatAutocompleteSelectedEvent} from '@angular/material/autocomplete';
import {AffiliateApiService} from '../../core/services/api/affiliate-api.service';
import {outPlaceSort} from '../../core/pure-utils/array-utils';
import {stripHttpWWWFromUrl} from '../../core/pure-utils/string-utils';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {EditAdsDialogComponent} from './merchant-ads/edit-ads-dialog/edit-ads-dialog.component';
import {CreateAdDialogComponent} from './merchant-ads/create-ad-dialog/create-ad-dialog.component';
import {TagApiService} from '../../core/services/api/tag-api.service';
import {ArchiveAdDialogComponent} from './merchant-ads/archive-ad-dialog/archive-ad-dialog.component';
import {SnackBarService} from '../../core-services/snack-bar/snack-bar.service';
import {triggerHotJarGa} from '../../core/pure-utils/analytics';
import {DashboardPermissionsService} from '../../dashboard/dashboard-permissions-service/dashboard-permissions.service';
import {ActivatedRoute, Router} from '@angular/router';
import {environment} from '../../../environments/environment';
import {DashboardHttpService} from '../../core-services/dashboard-http-service/dashboard-http.service';
import {MerchantAdsComponent} from './merchant-ads/merchant-ads.component';
import {MatCheckboxChange} from '@angular/material/checkbox';
import {takeUntilDestroyed, toObservable} from '@angular/core/rxjs-interop';
import {SelectedItemsData, SelectedItemsDialogComponent} from '../../shared/selected-items-dialog/selected-items-dialog.component';

@Component({
    selector: 'app-ad-management',
    templateUrl: './ad-management.component.html',
    styleUrls: ['./ad-management.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AdManagementComponent implements OnInit, OnDestroy {

  constructor(private dashboardApiService: DashboardApiService, private changeDetectorRef: ChangeDetectorRef, private media: MediaMatcher,
              private merchantApiService: MerchantApiService, private adManagementService: AdManagementService,
              private fb: UntypedFormBuilder, private affiliateApiService: AffiliateApiService, private dialog: MatDialog, private tagApiService: TagApiService,
              private snackBarService: SnackBarService, private dashboardPermissionsService: DashboardPermissionsService, private router: Router,
              private dashboardHttpService: DashboardHttpService, private route: ActivatedRoute) {
  }

  @ViewChild(MerchantAdsComponent) merchantAdsComponent;
  public triggerAnalytics = triggerHotJarGa;
  ngOnInit$ = new Subject<void>();
  masterToggleChecked = signal<{checked: boolean}>({checked: false});
  masterToggleState = signal<MasterToggleState>('unchecked');

  private accountPermissions$Sub = this.dashboardPermissionsService.accountWithPermissions$
    .subscribe(([currentAccount, accountPermissions]) => {
      let foundPermission = accountPermissions.some((singlePermission) =>
        (singlePermission.permission_name === 'configure_tools' && singlePermission.has_permission)
        || (singlePermission.permission_name === 'merchant_access' && singlePermission.has_permission));
      if (!foundPermission) {
        this.router.navigate(['/access-denied']);
      }
    });
  private checkForQueryParams$Sub = this.ngOnInit$.pipe(
    switchMap(() => combineLatest([this.route.queryParamMap.pipe(
      filter(queryParams => queryParams.has('merchantAdId'))
    ), this.dashboardApiService.currentAccount$.pipe(filter(currentAccount => currentAccount.entity_group_name === 'affiliates'))])),
    switchMap(([queryParams, currentAccount]) =>
      this.affiliateApiService.getAffiliateMerchantAdById(currentAccount.entity_id, queryParams.get('merchantAdId'))),
    first()
  ).subscribe( {
    next: (merchantAd) => this.merchantAdsComponent.onGetLinkClick(merchantAd),
    error: () => this.snackBarService.openErrorSnackBar('Unable to find merchant ad.')
  });

  opened = true;
  private userAffiliates: AffiliateExclusiveOption[];
  private userTagGroups: TagExclusiveOption[];
  allMerchants = this.adManagementService.allMerchants;
  supportedGroups: EntityGroupName[] = ['merchants', 'affiliates'];
  triggerUserTagRefresh$ = new BehaviorSubject<void>(undefined);
  currentAccountGroup$ = this.dashboardApiService.currentAccountGroup$.pipe(
    tap(() => this.formGroup.reset(this.initialControlState, {emitEvent: false})),
    shareReplay({refCount: true, bufferSize: 1}));
  groupSupported$ = this.currentAccountGroup$.pipe(map(x => this.supportedGroups.includes(x)));
  mobileQueryListener = () => this.changeDetectorRef.detectChanges();
  mobileQuery = this.media.matchMedia('(max-width: 374.99px)');
  merchantAdsSelected$ = new BehaviorSubject<MerchantAd[]>([]);
  requestParams: AffiliateAdRequest | MerchantAdRequest;

  initialControlState: FilterMap = {
    imageWidth: null,
    imageHeight: null,
    adType: 'all',
    adTitle: '',
    classicMerchantAdId: null,
    dynamicLabel: 'all',
    status: 'active',
    merchant: this.allMerchants,
  };

  controls = {
    imageWidth: this.fb.control(this.initialControlState.imageWidth),
    imageHeight: this.fb.control(this.initialControlState.imageHeight),
    adType: this.fb.control(this.initialControlState.adType),
    adTitle: this.fb.control(this.initialControlState.adTitle),
    classicMerchantAdId: this.fb.control(this.initialControlState.classicMerchantAdId),
    dynamicLabel: this.fb.control(this.initialControlState.dynamicLabel),
    status: this.fb.control(this.initialControlState.status),
    merchant: this.fb.control(this.initialControlState.merchant)
  };
  formGroup = this.fb.group(this.controls);

  controlValues = {
    imageWidth$: this.controls.imageWidth.valueChanges.pipe(startWith<FilterMap['imageWidth']>(this.initialControlState.imageWidth), debounceTime(300)),
    imageHeight$: this.controls.imageHeight.valueChanges.pipe(startWith<FilterMap['imageHeight']>(this.initialControlState.imageHeight), debounceTime(300)),
    adType$: this.controls.adType.valueChanges.pipe(startWith<FilterMap['adType']>(this.initialControlState.adType)),
    adTitle$: this.controls.adTitle.valueChanges.pipe(startWith<FilterMap['adTitle']>(this.initialControlState.adTitle), debounceTime(300)),
    classicMerchantAdId$:
      this.controls.classicMerchantAdId.valueChanges.pipe(startWith<FilterMap['classicMerchantAdId']>(this.initialControlState.classicMerchantAdId)),
    dynamicLabel$: this.controls.dynamicLabel.valueChanges.pipe(startWith<FilterMap['dynamicLabel']>(this.initialControlState.dynamicLabel)),
    status$: this.controls.status.valueChanges.pipe(startWith<FilterMap['status']>(this.initialControlState.status)),
    merchant$: this.controls.merchant.valueChanges.pipe(startWith<FilterMap['merchant']>(this.initialControlState.merchant)),
  };

  readonly displayNameMap: { [key in keyof FilterMap]: string } = {
    imageWidth: 'Image Width',
    imageHeight: 'Image Height',
    adType: 'Ad Type',
    adTitle: 'Ad Title',
    classicMerchantAdId: 'Ad ID',
    dynamicLabel: 'Ad Label',
    status: 'Ad Status',
    merchant: 'Merchant'
  };
  filterChips = signal([] as FilterChip[]);
  removeChip(chip: FilterChip) {
    if (chip.control === 'status') {
      this.controls['status'].setValue('all');
    } else {
      this.controls[chip.control].setValue(this.initialControlState[chip.control]);
    }
  }

  filterMap = signal({} as FilterMap);
  resetSelectedMerchantAds$Sub = combineLatest([toObservable(this.filterMap), this.currentAccountGroup$])
    .subscribe(() => {
      this.masterToggleChecked.set({checked: false});
      this.masterToggleState.set('unchecked');
    });
  filterMap$ = combineLatest([
    this.controlValues.imageWidth$,
    this.controlValues.imageHeight$,
    this.controlValues.adTitle$,
    this.controlValues.classicMerchantAdId$.pipe(debounceTime(300)),
    this.controlValues.dynamicLabel$, this.controlValues.adType$,
    this.controlValues.status$,
    (<Observable<MerchantCommon>> this.controlValues.merchant$.pipe(filter(x => typeof x !== 'string' || x === this.allMerchants)))
  ]).pipe(
    map(([imageWidth, imageHeight, adTitle, classicMerchantAdId, dynamicLabel, adType, status, merchant]) =>
      ({imageWidth, imageHeight, adTitle, classicMerchantAdId, dynamicLabel, adType, status, merchant}))
  );

  filterMap$Sub = this.currentAccountGroup$.pipe(
    switchMap((currentAccount) =>
      this.filterMap$.pipe(distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)), map(filterMap => [filterMap, currentAccount] as const))))
    .subscribe(([filterMap, currentAccountGroup]) => {
      this.filterMap.set(filterMap);
      let filterChips = Object.keys(filterMap)
        .filter(filterChip => {
          if (currentAccountGroup === 'merchants') {
            return filterChip !== 'merchant';
          } else {
            return filterChip !== 'status';
          }
        })
        .reduce((acc, key) => {
          if ((filterMap[key] && filterMap[key] !== 'all') && (filterMap[key] !== this.allMerchants)) {
            let value = key === 'merchant' ? filterMap[key].merchant_name : filterMap[key].toString();
            acc.push({
              displayTitle: this.displayNameMap[key],
              value,
              control: key
            });
          }
          return acc;
        }, [] as FilterChip[]);
      this.filterChips.set(filterChips);
    });

  userAffiliates$Sub = this.dashboardApiService.usersAffiliates$.subscribe(affiliates => this.userAffiliates = affiliates);
  userTagGroups$Sub = this.tagApiService.usersTagGroups$.subscribe(tags => this.userTagGroups = tags);

  archiveEditSnackBarRef: MatDialogRef<SelectedItemsDialogComponent<MerchantAd>, boolean>;
  openArchiveEditDialog$Sub = this.merchantAdsSelected$.pipe(
    filter((merchantAds) => merchantAds.length > 0 && this.archiveEditSnackBarRef === undefined),
    switchMap((merchantAds) => {
      let selectedItemsData: SelectedItemsData<MerchantAd> = {
        descriptionTitle: 'Ads Selected ',
        buttons: [
          {display: 'Edit', associatedFunc: this.openEditAdsDialog, triggerAnalytics:{triggerName: 'AdManagement_BulkEdit_icon', eventCategory: 'AdManagement'}},
          {display: 'Archive', associatedFunc: this.openArchiveAdsDialog}
        ],
        items$: this.merchantAdsSelected$
      };
      this.archiveEditSnackBarRef = this.dialog.open(SelectedItemsDialogComponent<MerchantAd>, {
        data: selectedItemsData,
        hasBackdrop: false,
        panelClass: 'selection-dialog-panel',
        position: {bottom: '4px', left:'calc(50% - 200px)'},
        height:'64px',
        width: '400px',
        minWidth: '400px'
      });
      return this.archiveEditSnackBarRef.afterClosed();
    }),
    takeUntilDestroyed()
  ).subscribe((selectedMerchantAds) => {
    this.archiveEditSnackBarRef = undefined;
    this.merchantAdsComponent.selection.clear();
  });
  closeArchiveEditDialog$Sub = this.merchantAdsSelected$.pipe(
    filter((merchantAds) => merchantAds.length === 0 && this.archiveEditSnackBarRef !== undefined),
    takeUntilDestroyed()
  ).subscribe((selectedMerchantAds) => {
    this.archiveEditSnackBarRef.close();
  });

  openEditAdsDialog = (merchantAds: MerchantAd[]) => {
    let dialogRef = this.dialog.open(EditAdsDialogComponent, {
      data: {merchantAds, userAffiliates: this.userAffiliates, userTagGroups: this.userTagGroups},
      disableClose: true,
      width: '100%',
      maxWidth: '90vw'
    });

    dialogRef.beforeClosed().subscribe((saved: boolean) => {
      if (saved) {
        this.archiveEditSnackBarRef?.close();
      }
    });

    dialogRef.afterClosed().subscribe((saved: boolean) => {
      if (saved) {
        // cause table to refresh
        this.filterMap.set({ ...this.filterMap() });
        this.triggerUserTagRefresh$.next(undefined);
      }
    });
  };

  openCreateAdDialog(): void {
    let dialogRef = this.dialog.open(CreateAdDialogComponent, {});
    dialogRef.beforeClosed().subscribe((saved: boolean) => {
      if (saved) {
        this.archiveEditSnackBarRef?.close();
      }
    });

    dialogRef.afterClosed().subscribe((saved: boolean) => {
      if (saved) {
        // cause table to refresh
        this.filterMap.set({ ...this.filterMap() });
        this.triggerUserTagRefresh$.next(undefined);
      }
    });
  }

  openArchiveAdsDialog = (merchantAds: MerchantAd[]) => {
    let dialogRef = this.dialog.open(ArchiveAdDialogComponent);

    dialogRef.afterClosed().subscribe((archive: boolean) => {
      if (archive) {
        let merchantAdsUpdateRequests = merchantAds.map(merchantAd => ({
          merchant_id: merchantAd.merchant.merchant_id,
          merchant_ad_id: merchantAd.merchant_ad_id,
          end_date: new Date()
        }));
        this.merchantApiService.editMultipleMerchantAds(merchantAdsUpdateRequests).subscribe({
          next: (updatedMerchantAds) => {
            let isUpdateUnsuccessful = updatedMerchantAds.some(merchantAd => !merchantAd.success);
            if (!isUpdateUnsuccessful) {
              this.snackBarService.openSnackBar('Ad(s) were successfully archived.');
            } else {
              this.snackBarService.openErrorSnackBar('Some of your ads were not archived.');
            }
            // cause table to refresh
            this.filterMap.set({...this.filterMap()});
            this.triggerUserTagRefresh$.next(undefined);
          },
          error: (error) => {
            this.snackBarService.openErrorSnackBar(error.error.error);
          }
        });
      }
    });
  };

  /** Merchants Autocomplete Start */
  readonly filteredLimit = 250;
  merchantFilterPrevious = this.allMerchants;

  merchantFilterOptions$: Observable<MerchantCommon[]> =
  this.dashboardApiService.currentAccount$.pipe(
    switchMap((currentAccount) => {
      if (currentAccount.entity_group_name !== 'affiliates') {
        return of<AffiliateRelationshipMerchant[]>([]);
      }

      return this.affiliateApiService.getAllAssociatedMerchantWebsitesOfAffiliate([currentAccount.entity_id], 'active').pipe(
        map(xs => xs.filter(x => x.is_default && x.merchant_status === 'active')));
    }),
    map(xs => outPlaceSort(xs, x => x.merchant_name)
      .map(x => ({...x, merchantWebsiteUrlStripped: stripHttpWWWFromUrl(x.merchant_website_url)}))),
    shareReplay({bufferSize: 1, refCount: true}));

  merchantFilteredOptions$ = combineLatest([this.controlValues.merchant$, this.merchantFilterOptions$]).pipe(
    map(([merchantFilter, merchantOptions]) => {
      if (merchantFilter === '' || typeof merchantFilter !== 'string') {
        return merchantOptions;
      }

      let lowerInput = merchantFilter.toLowerCase();
      return merchantOptions.filter(x => x.merchant_name.toLowerCase().includes(lowerInput) ||
          x.merchantWebsiteUrlStripped.includes(lowerInput) || String(x.classic_merchant_id).includes(lowerInput));
    }),
    observeOn(asyncScheduler),
    shareReplay({bufferSize: 1, refCount: true}));

  merchantFilteredOptionsLimited$ = this.merchantFilteredOptions$.pipe(
    map(x => x.length > this.filteredLimit ? x.slice(0, this.filteredLimit) : x));

  autoSelectOnOpened = (formControl: UntypedFormControl) => formControl.setValue('');

  merchantFilterAutoCompleteOnClosed = () => {
    if (typeof this.controls.merchant.value === 'string') {
      this.controls.merchant.setValue(this.merchantFilterPrevious);
    }
  };

  merchantFilterAutoCompleteOnSelected = ($event: MatAutocompleteSelectedEvent, filteredMerchantsInput: HTMLInputElement) => {
    this.merchantFilterPrevious = $event.option.value;
    filteredMerchantsInput.blur();
  };

  merchantFilterDisplayWith = (value: typeof AdManagementComponent.prototype.initialControlState['merchant']) =>
    typeof value === 'string' ? value === this.allMerchants ? 'All Merchants' : value : value.merchant_name.trim();
  /** Merchants Autocomplete End */

  ngOnInit() {
    this.mobileQuery.addEventListener('change', this.mobileQueryListener);
    this.ngOnInit$.next(null);
    this.ngOnInit$.complete();
  }

  ngOnDestroy() {
    this.mobileQuery.removeEventListener('change', this.mobileQueryListener);
    this.userAffiliates$Sub.unsubscribe();
    this.userTagGroups$Sub.unsubscribe();
    this.filterMap$Sub.unsubscribe();
    this.accountPermissions$Sub.unsubscribe();
    this.openArchiveEditDialog$Sub.unsubscribe();
    this.closeArchiveEditDialog$Sub.unsubscribe();
    this.checkForQueryParams$Sub.unsubscribe();
    this.resetSelectedMerchantAds$Sub.unsubscribe();

    if (this.archiveEditSnackBarRef !== undefined) {
      this.archiveEditSnackBarRef.close();
    }
  }

  downloadMerchantAds() {
    const columns: DownloadRequestContentColumn[] = [
      {column: 'image_url', header: 'Banner Ad'},
      {column: 'link_text', header: 'Text Ad'},
      {column: 'ad_type', header: 'Ad Type'},
      {column: 'ad_title', header: 'Ad Name'},
      {column: 'destination_url', header: 'Destination URL'},
      {column: 'classic_merchant_ad_id', header: 'Ad ID'},
      {column: 'calculated_active_websites', header: 'Active Affiliates'},
      {column: 'calculated_conversion_rate', header: 'Conversion Rate'},
      {column: 'promotion_details', header: 'Promotion Type'},
      {column: 'start_date', header: 'Start Date'},
      {column: 'end_date', header: 'End Date'},
      {column: 'updated_at', header: 'Updates'}
    ];

    let params = { ...this.requestParams,
      sort: this.requestParams.sort && this.requestParams.direction ? 'ma.' + this.requestParams.sort : '',
      direction: this.requestParams.direction?.toUpperCase() ?? ''
    };
    // Remove the page and limit property from params.
    let {page, limit, ...updateParams} = params;

    let paramsForURL = new URLSearchParams(Object.entries(updateParams) as string[][]).toString();
    let downloadRequest$Sub = this.dashboardApiService.currentAccount$.pipe(
      switchMap(currentAccount => {
        let requestContent: DownloadRequestContent = {
          request_url: `/merchants/${currentAccount.entity_id}/ads?${paramsForURL}`,
          columns,
          format: 'csv'
        };
        let downloadRequestBody: DownloadRequestBody = {
          request: JSON.stringify(requestContent)
        };
        return this.dashboardHttpService.downloadPostBlobWithOptions(`${environment.apiUrl}/download`, downloadRequestBody, 'merchant-ads');
      }),
      first()
    ).subscribe();
  }

  requestParamChange($event: AffiliateAdRequest | MerchantAdRequest) {
    this.requestParams = $event;
    this.triggerUserTagRefresh$.next(undefined);
  }

  onMasterToggleChange($event: MatCheckboxChange) {
    this.masterToggleChecked.set({checked: $event.checked});
    this.masterToggleState.set($event.checked ? 'checked' : 'unchecked');
  }

  onMasterToggleStateOutput($event: MasterToggleState) {
    this.masterToggleState.set($event);
  }
}
